import React, { useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/design-system/components/Button/Button";
import { Select } from "@/design-system/components/Select/Select";
import { toast } from "@/design-system/components/Toast/Toast";
import { ToggleChoice } from "@/design-system/components/ToggleChoice/ToggleChoice";
import { Typography } from "@/design-system/components/Typography/Typography";
import { DestinationKey } from "@/destinations/types";
import { TransformDefinitionSet } from "@/transforms/transformationDefinition";
import {
  createTransformationSet,
  NewTransformationSet,
} from "@/transforms/transformations";
import { LoadedEventDefinition } from "@/types/global";

const ALL_EVENTS = "all-events";

type SetKind = "track" | "identify";

const kindOptions: { value: SetKind; label: string }[] = [
  { value: "track", label: "Events" },
  { value: "identify", label: "Identify calls" },
];

type NewTransformationSetFormProps = {
  destinationKey: DestinationKey;
  /** Events sent to the destination, offered as the set's event filter. */
  events: LoadedEventDefinition[];
  /** Sets that already apply to this destination. */
  existingSets: TransformDefinitionSet[];
  onCreated: (transformationSetId: string) => void;
  onOpenExisting: (transformationSetId: string) => void;
  onCancel: () => void;
};

export function NewTransformationSetForm({
  destinationKey,
  events,
  existingSets,
  onCreated,
  onOpenExisting,
  onCancel,
}: NewTransformationSetFormProps) {
  const [kind, setKind] = useState<SetKind>("track");
  const [eventDefId, setEventDefId] = useState(ALL_EVENTS);
  const [saving, setSaving] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    formRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, []);

  const eventOptions = useMemo(
    () => [
      { id: ALL_EVENTS, name: "All events" },
      ...events.map(({ id, name }) => ({ id, name })),
    ],
    [events],
  );

  const newSet: NewTransformationSet =
    kind === "identify"
      ? {
          intention: "identifyEvent",
          destinationName: destinationKey,
          eventDefId: null,
          eventType: "identify",
        }
      : {
          intention: "fixBadData",
          destinationName: destinationKey,
          eventDefId: eventDefId === ALL_EVENTS ? null : eventDefId,
          eventType: "track",
        };

  const duplicate = existingSets.find(
    (set) =>
      set.intention === newSet.intention &&
      set.eventDefId === newSet.eventDefId,
  );

  async function create() {
    setSaving(true);
    let transformationSetId: string;
    try {
      transformationSetId = await createTransformationSet(newSet);
    } catch (error) {
      toast.error("Couldn’t create the transformation. Try again.");
      console.error(error);
      setSaving(false);
      return;
    }
    onCreated(transformationSetId);
  }

  return (
    <div
      ref={formRef}
      className="border-borderSubtle flex flex-col gap-4 rounded-lg border p-4"
    >
      <Typography variant="label-md">New transformation</Typography>
      <ToggleChoice
        className="w-fit"
        ariaLabel="Transform"
        options={kindOptions}
        getOptionLabel={(option) => option.label}
        getOptionValue={(option) => option.value}
        value={kindOptions.find((option) => option.value === kind)}
        handleChange={(option) => setKind(option.value)}
      />
      {kind === "track" && (
        <Select
          options={eventOptions}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.id}
          searchable
          searchPlaceholder="Search events"
          rootProps={{ value: eventDefId, onValueChange: setEventDefId }}
          triggerProps={{ "aria-label": "Event" }}
        />
      )}
      {duplicate && (
        <Typography variant="body-sm" color="subtle">
          A transformation set for this already exists.{" "}
          <button
            type="button"
            className="text-textActionSelected cursor-pointer underline"
            onClick={() => onOpenExisting(duplicate.id)}
          >
            Open it
          </button>
        </Typography>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          onClick={create}
          loading={saving}
          disabled={!!duplicate}
          tooltipContent={
            duplicate
              ? "A transformation set for this already exists"
              : undefined
          }
        >
          Create
        </Button>
      </div>
    </div>
  );
}
